import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { makeStyles, useTheme, alpha, lighten } from "@material-ui/core/styles";
import {
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  useMediaQuery,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import AddIcon from "@material-ui/icons/Add";
import BarChartIcon from "@material-ui/icons/BarChart";
import TuneIcon from "@material-ui/icons/Tune";
import SearchIcon from "@material-ui/icons/Search";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import moment from "moment";
import Board from "react-trello";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import OpportunityModal from "../../components/OpportunityModal";
import PipelineModal from "../../components/PipelineModal";
import ContactAvatar from "../../components/ContactAvatar";
import { AuthContext } from "../../context/Auth/AuthContext";
import { FormDialogHeader, useFormDialogStyles } from "../../components/FormDialog";
import {
  useKanbanDndStyles,
  boardZoomStyle,
  DropHighlightLane,
  MetaLaneHeader,
} from "../../components/KanbanDnd";

const ZOOM_KEY = "crmKanbanZoom";
const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.0;
const EMPTY_CARD_PREFIX = "__empty__";
const MAX_PAGES = 10; // 40 por página → até 400 negócios no board
const IDLE_DAYS = 7; // "parado" a partir de quantos dias sem atualização
const QUICK_LOST_REASONS = ["Preço", "Escolheu concorrente", "Sem resposta", "Sem orçamento"];

const formatBRL = (value) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const initialsOf = (name = "") => {
  const parts = name.replace(/[^A-Za-zÀ-ÿ\s]/g, " ").trim().split(/\s+/);
  return ((parts[0] || "?")[0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
};

// situação do negócio aberto: atrasado (passou da previsão) e/ou parado
const dealAlerts = (opportunity) => {
  if (opportunity.status && opportunity.status !== "open") return {};
  const today = moment().startOf("day");
  const lateDays = opportunity.expectedCloseDate
    ? today.diff(moment(opportunity.expectedCloseDate, "YYYY-MM-DD"), "days")
    : null;
  const idleDays = moment().diff(moment(opportunity.updatedAt), "days");
  return {
    late: lateDays !== null && lateDays > 0 ? lateDays : 0,
    idle: idleDays >= IDLE_DAYS ? idleDays : 0,
  };
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    // o CssBaseline do app fica fora do ThemeProvider: sem isso o texto
    // herda o preto do tema claro no tema escuro
    color: theme.palette.text.primary,
    // no celular o topo não cabe junto com o board: a página rola e o board
    // mantém uma altura mínima (antes ficava espremido no rodapé)
    [theme.breakpoints.down("xs")]: {
      overflowY: "auto",
      overflowX: "hidden",
      // largura dos cards/colunas no celular: uma coluna quase da tela toda,
      // com a ponta da próxima aparecendo para indicar que dá para rolar
      "--kanban-lane-width": "min(300px, calc(100vw - 90px))",
    },
  },
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3, 2),
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2, 1),
    },
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    flexWrap: "wrap",
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    [theme.breakpoints.down("xs")]: { fontSize: 22 },
  },
  subtitle: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  pipelineSelect: {
    minWidth: 170,
    "& .MuiOutlinedInput-root": { borderRadius: 8, height: 34, fontSize: 13 },
  },
  headerButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: { marginLeft: "auto", flexWrap: "nowrap" },
  },
  headerButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 40,
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down("xs")]: { height: 36, padding: theme.spacing(0, 1.5) },
  },
  iconOnlyButton: {
    minWidth: 36,
    width: 36,
    padding: 0,
  },
  secondaryButton: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
  },
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 3, 2),
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
    // celular: faixa única que rola para o lado, em vez de 2 linhas de cards
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      overflowX: "auto",
      gap: theme.spacing(1),
      padding: theme.spacing(0, 2, 1.5),
      scrollSnapType: "x proximity",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": { display: "none" },
    },
  },
  kpi: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      flex: "0 0 auto",
      minWidth: 132,
      maxWidth: 180,
      padding: theme.spacing(1, 1.5),
      borderRadius: 10,
      scrollSnapAlign: "start",
    },
  },
  kpiClickable: {
    cursor: "pointer",
    textAlign: "left",
    font: "inherit",
    color: "inherit",
    "&:hover": { borderColor: theme.palette.text.secondary },
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("xs")]: { fontSize: 10, whiteSpace: "nowrap" },
  },
  kpiValue: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.3,
    [theme.breakpoints.down("xs")]: { fontSize: 17, whiteSpace: "nowrap" },
  },
  kpiCaption: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("xs")]: {
      fontSize: 11,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    padding: theme.spacing(0, 3, 1.5),
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(1),
      padding: theme.spacing(0, 2, 1),
    },
  },
  searchField: {
    width: 300,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      height: 38,
    },
    [theme.breakpoints.down("xs")]: { width: "100%" },
  },
  ownerSelect: {
    minWidth: 200,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      height: 38,
    },
    [theme.breakpoints.down("xs")]: { flex: 1, minWidth: 0 },
  },
  zoomGroup: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    marginLeft: "auto",
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("xs")]: { marginLeft: 0 },
  },
  zoomLabel: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    minWidth: 34,
    textAlign: "center",
    userSelect: "none",
  },
  boardWrapper: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
    // no celular o board ocupa quase a tela inteira ao rolar até ele
    [theme.breakpoints.down("xs")]: {
      flex: "0 0 auto",
      height: "calc(100vh - 120px)",
      minHeight: 420,
    },
    "& *::-webkit-scrollbar": { width: 4, height: 4 },
    "& *::-webkit-scrollbar-thumb": {
      borderRadius: 4,
      backgroundColor: "transparent",
    },
    "& *:hover::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.grey[500],
    },
    "& *::-webkit-scrollbar-track": { background: "transparent" },
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.7)",
    zIndex: 10,
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: "12px",
    marginBottom: 8,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
      transform: "translateY(-1px)",
    },
    transition: "box-shadow 0.15s ease, transform 0.15s ease",
    width: "var(--kanban-lane-width, 240px)",
    minWidth: "var(--kanban-lane-width, 240px)",
    maxWidth: "var(--kanban-lane-width, 240px)",
    boxSizing: "border-box",
    overflow: "hidden",
    whiteSpace: "normal", // o react-trello força nowrap nas colunas
  },
  emptyCard: {
    width: "var(--kanban-lane-width, 240px)",
    minWidth: "var(--kanban-lane-width, 240px)",
    maxWidth: "var(--kanban-lane-width, 240px)",
    padding: "20px 0",
    textAlign: "center",
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    userSelect: "none",
    cursor: "default",
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 4,
  },
  cardTitle: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: "19px",
    color: theme.palette.text.primary,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word",
  },
  cardUntitled: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontWeight: 500,
  },
  cardContact: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    fontSize: 13,
    color: theme.palette.text.secondary,
    "& span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  cardValue: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#8EC0FF" : theme.palette.primary.main,
  },
  cardReason: {
    fontSize: 12,
    color: theme.palette.type === "dark" ? "#FFB4AE" : theme.palette.error.main,
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  badge: {
    height: 22,
    padding: "0 8px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  owner: {
    marginLeft: "auto",
    width: 24,
    height: 24,
    borderRadius: "50%",
    fontSize: 11,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
    color: theme.palette.type === "dark" ? "#CFE0FA" : theme.palette.primary.dark,
    flexShrink: 0,
  },
  openIcon: {
    padding: 2,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    "&:hover": { color: theme.palette.primary.main },
  },
  reasonChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
}));

// ---------------------------------------------------------------------------
// Card do board
// ---------------------------------------------------------------------------
// negócio criado pela automação de entrada nasce com o título igual ao nome
// do contato — nesse caso repetir o nome no card só ocupa espaço
const isUntitled = (opportunity) => {
  const contactName = opportunity.contact?.name?.trim();
  return !!contactName && contactName === opportunity.title?.trim();
};

const Badge = ({ classes, color, children, title }) => (
  <span
    className={classes.badge}
    title={title}
    style={{ color, backgroundColor: alpha(color, 0.16) }}
  >
    {children}
  </span>
);

const OpportunityCard = ({ description, classes, history, onEdit, tone }) => {
  const { opportunity, isEmpty } = description || {};

  if (isEmpty) {
    return <div className={classes.emptyCard}>Nenhum negócio nesta etapa</div>;
  }

  const untitled = isUntitled(opportunity);
  const { late, idle } = dealAlerts(opportunity);
  const status = opportunity.status || "open";
  const hasValue = opportunity.value != null && opportunity.value !== "" && Number(opportunity.value) > 0;

  return (
    <div className={classes.card} onClick={() => onEdit(opportunity)}>
      <div className={classes.cardHeader}>
        <Typography
          className={`${classes.cardTitle} ${untitled ? classes.cardUntitled : ""}`}
          title={opportunity.title}
        >
          {untitled ? "Novo negócio" : opportunity.title}
        </Typography>
        <Tooltip title="Abrir ficha do contato">
          <IconButton
            className={classes.openIcon}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              history.push(`/contacts/${opportunity.contactId}/profile`);
            }}
          >
            <OpenInNewIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.cardContact}>
        <ContactAvatar
          src={opportunity.contact?.profilePicUrl}
          name={opportunity.contact?.name}
          style={{ width: 20, height: 20, fontSize: 10 }}
        />
        <span>{opportunity.contact?.name}</span>
      </div>

      {hasValue && (
        <Typography className={classes.cardValue}>{formatBRL(opportunity.value)}</Typography>
      )}

      {status === "lost" && opportunity.lostReason && (
        <Typography className={classes.cardReason}>Motivo: {opportunity.lostReason}</Typography>
      )}

      <div className={classes.cardFooter}>
        {late > 0 ? (
          <Badge classes={classes} color={tone("#E53935")} title="Passou da previsão de fechamento">
            Atrasado {late}d
          </Badge>
        ) : (
          status === "open" &&
          opportunity.expectedCloseDate && (
            <Badge classes={classes} color={tone("#78909C")} title="Previsão de fechamento">
              Fecha {moment(opportunity.expectedCloseDate, "YYYY-MM-DD").format("DD/MM")}
            </Badge>
          )
        )}
        {idle > 0 && (
          <Badge classes={classes} color={tone("#F59E0B")} title={`Sem atualização há ${idle} dias`}>
            Parado {idle}d
          </Badge>
        )}
        {untitled && status === "open" && (
          <Badge classes={classes} color={tone("#78909C")} title="Clique para dar título e valor">
            Sem título e valor
          </Badge>
        )}
        {status === "won" && opportunity.closedAt && (
          <Badge classes={classes} color={tone("#43A047")}>
            Ganho em {moment(opportunity.closedAt).format("DD/MM")}
          </Badge>
        )}
        {opportunity.user?.name ? (
          <Tooltip title={`Responsável: ${opportunity.user.name}`}>
            <span className={classes.owner}>{initialsOf(opportunity.user.name)}</span>
          </Tooltip>
        ) : (
          <span style={{ flex: 1 }} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Página CRM — board de negócios por estágio do pipeline
// ---------------------------------------------------------------------------
const OpportunityKanban = () => {
  const classes = useStyles();
  const dndClasses = useKanbanDndStyles();
  const dialogClasses = useFormDialogStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.profile === "admin";
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const [pipelines, setPipelines] = useState([]);
  const [pipelineId, setPipelineId] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [onlyAttention, setOnlyAttention] = useState(false);
  // etapa escolhida pelo "+" da coluna ao criar um negócio
  const [newDealStageId, setNewDealStageId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(() => {
    const saved = parseFloat(localStorage.getItem(ZOOM_KEY));
    return !isNaN(saved) ? saved : 1.0;
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState(null);
  const [pipelineModalOpen, setPipelineModalOpen] = useState(false);

  // diálogo de motivo da perda (exigido pelo backend em estágio "lost")
  const [lostDialog, setLostDialog] = useState(null); // { opportunityId, stageId, prevStageId }
  const [lostReason, setLostReason] = useState("");

  // Só a resposta da busca mais recente é aplicada: com várias buscas em
  // paralelo (socket + drag), uma resposta antiga poderia desfazer o
  // movimento otimista que acabou de ser feito.
  const fetchSeq = useRef(0);

  // `silent` atualiza sem cobrir o board com o loading — usado depois de
  // arrastar, salvar e nos eventos de socket.
  const fetchData = useCallback(async (selectedPipelineId, { silent = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!silent) setLoading(true);
    try {
      const { data: pipelinesData } = await api.get("/pipelines");
      if (seq !== fetchSeq.current) return;
      setPipelines(pipelinesData);

      let currentId = selectedPipelineId;
      if (!currentId) {
        const def = pipelinesData.find((p) => p.isDefault) || pipelinesData[0];
        currentId = def?.id || null;
        setPipelineId(currentId);
      }
      if (!currentId) {
        setOpportunities([]);
        return;
      }

      const all = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const { data } = await api.get("/opportunities", {
          params: { pipelineId: currentId, pageNumber: page },
        });
        all.push(...(data.opportunities || []));
        if (!data.hasMore) break;
      }
      if (seq !== fetchSeq.current) return;
      setOpportunities(all);
    } catch (err) {
      toastError(err);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(pipelineId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data } = await api.get("/users/");
        setUsers(data.users || []);
      } catch (err) {
        // filtro por responsável é opcional
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    const refresh = (data) => {
      if (["update", "create", "delete"].includes(data.action)) {
        fetchData(pipelineId, { silent: true });
      }
    };
    socket.on(`company-${companyId}-opportunity`, refresh);
    socket.on(`company-${companyId}-pipeline`, refresh);

    return () => socket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId]);

  const currentPipeline = pipelines.find((p) => p.id === pipelineId);
  const stages = (currentPipeline?.stages || [])
    .slice()
    .sort((a, b) => a.order - b.order);

  const query = searchQuery.trim().toLowerCase();
  const visibleOpportunities = opportunities.filter((o) => {
    if (filterUserId && o.userId !== filterUserId) return false;
    if (
      query &&
      !`${o.title || ""} ${o.contact?.name || ""}`.toLowerCase().includes(query)
    ) {
      return false;
    }
    if (onlyAttention) {
      const { late, idle } = dealAlerts(o);
      if (!(late > 0 || idle > 0)) return false;
    }
    return true;
  });

  const handleZoom = (delta) => {
    setZoom((prev) => {
      const next = Math.min(
        ZOOM_MAX,
        Math.max(ZOOM_MIN, Math.round((prev + delta) * 10) / 10)
      );
      localStorage.setItem(ZOOM_KEY, String(next));
      return next;
    });
  };

  const handleEdit = useCallback((opportunity) => {
    setEditingOpportunity(opportunity);
    setModalOpen(true);
  }, []);

  const handleModalClose = (saved) => {
    setModalOpen(false);
    setEditingOpportunity(null);
    setNewDealStageId(null);
    if (saved) fetchData(pipelineId, { silent: true });
  };

  // Atualização otimista: o card já fica no estágio novo (com contador e
  // total corretos) sem esperar a API.
  const setOpportunityStage = (opportunityId, stageId) => {
    setOpportunities((prev) =>
      prev.map((o) => (o.id === opportunityId ? { ...o, stageId } : o))
    );
  };

  const moveToStage = async (opportunityId, stageId, reason) => {
    setOpportunityStage(opportunityId, stageId);
    try {
      await api.put(`/opportunities/${opportunityId}/stage`, {
        stageId,
        lostReason: reason,
      });
    } catch (err) {
      toastError(err);
    } finally {
      // confirma com o servidor (e desfaz o movimento se a API falhou)
      fetchData(pipelineId, { silent: true });
    }
  };

  const requestMove = (opportunityId, stageId) => {
    const stage = stages.find((s) => s.id === Number(stageId));
    if (!stage) return;
    if (stage.type === "lost") {
      // o card fica em "Perdido" enquanto o motivo é pedido; cancelar devolve
      const prevStageId = opportunities.find((o) => o.id === opportunityId)?.stageId;
      setOpportunityStage(opportunityId, stage.id);
      setLostReason("");
      setLostDialog({ opportunityId, stageId: stage.id, prevStageId });
      return;
    }
    moveToStage(opportunityId, stage.id);
  };

  const handleCardMove = (fromLaneId, toLaneId, cardId) => {
    if (cardId.startsWith(EMPTY_CARD_PREFIX)) return;
    if (fromLaneId === toLaneId) return;
    requestMove(Number(cardId), Number(toLaneId));
  };

  const confirmLost = () => {
    if (!lostReason.trim()) {
      toast.error("Informe o motivo da perda.");
      return;
    }
    moveToStage(lostDialog.opportunityId, lostDialog.stageId, lostReason.trim());
    setLostDialog(null);
  };

  const cancelLost = () => {
    if (lostDialog.prevStageId != null) {
      setOpportunityStage(lostDialog.opportunityId, lostDialog.prevStageId);
    }
    setLostDialog(null);
  };

  const openNewDeal = useCallback((stageId) => {
    setEditingOpportunity(null);
    setNewDealStageId(stageId || null);
    setModalOpen(true);
  }, []);

  // ---- indicadores do topo (calculados com o que o board já carregou) ----
  const kpis = useMemo(() => {
    const scoped = filterUserId ? opportunities.filter((o) => o.userId === filterUserId) : opportunities;
    const open = scoped.filter((o) => (o.status || "open") === "open");
    const openValue = open.reduce((sum, o) => sum + (Number(o.value) || 0), 0);
    const since = moment().subtract(30, "days");
    const closedRecently = scoped.filter(
      (o) => (o.status === "won" || o.status === "lost") && o.closedAt && moment(o.closedAt).isAfter(since)
    );
    const won = closedRecently.filter((o) => o.status === "won");
    const wonValue = won.reduce((sum, o) => sum + (Number(o.value) || 0), 0);
    const conversion = closedRecently.length
      ? Math.round((won.length / closedRecently.length) * 100)
      : null;
    const attention = open.filter((o) => {
      const { late, idle } = dealAlerts(o);
      return late > 0 || idle > 0;
    }).length;
    return { open, openValue, won, wonValue, conversion, closedRecently, attention };
  }, [opportunities, filterUserId]);

  const buildBoard = () => {
    const isDark = theme.palette.type === "dark";

    const lanes = stages.map((stage) => {
      const stageOpps = visibleOpportunities.filter((o) => o.stageId === stage.id);
      const total = stageOpps.reduce((sum, o) => sum + (Number(o.value) || 0), 0);
      const closed = stage.type === "won" || stage.type === "lost";

      const cards =
        stageOpps.length === 0
          ? [
              {
                id: `${EMPTY_CARD_PREFIX}${stage.id}`,
                title: "",
                description: { isEmpty: true },
                draggable: false,
              },
            ]
          : stageOpps.map((opportunity) => ({
              id: String(opportunity.id),
              title: opportunity.title,
              description: { opportunity },
              draggable: true,
            }));

      return {
        id: String(stage.id),
        title: stage.name,
        laneMeta: {
          color: stage.color || "#90A4AE",
          count: stageOpps.length,
          caption: total > 0 ? formatBRL(total) : "Sem valor",
          onAdd: closed ? null : () => openNewDeal(stage.id),
          addLabel: "Adicionar negócio nesta etapa",
        },
        style: {
          backgroundColor: closed
            ? isDark
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.02)"
            : theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderTop: `3px solid ${stage.color || "#90A4AE"}`,
          borderRadius: 12,
          height: "100%",
          maxHeight: "100%",
          padding: "10px 8px",
        },
        cards,
      };
    });

    return { lanes };
  };

  // Componentes estáveis entre renders: criados inline, cada render virava um
  // tipo novo de componente e todos os cards eram desmontados e montados de
  // novo (piscando) a cada atualização do board.
  const tone = useCallback(
    (c) => (theme.palette.type === "dark" ? lighten(c, 0.3) : c),
    [theme.palette.type]
  );
  const boardComponents = useMemo(
    () => ({
      Card: (props) => (
        <OpportunityCard
          {...props}
          classes={classes}
          history={history}
          onEdit={handleEdit}
          tone={tone}
        />
      ),
      LaneHeader: MetaLaneHeader,
      ScrollableLane: DropHighlightLane,
    }),
    [classes, history, handleEdit, tone]
  );

  const boardData = buildBoard();
  // zoom é só para telas grandes: no celular o board fica sempre em 100%
  const effectiveZoom = isMobile ? 1 : zoom;

  return (
    <div className={classes.root}>
      <OpportunityModal
        open={modalOpen}
        onClose={handleModalClose}
        opportunity={editingOpportunity}
        pipelineId={pipelineId}
        initialStageId={newDealStageId}
      />

      <PipelineModal
        open={pipelineModalOpen}
        onClose={(saved) => {
          setPipelineModalOpen(false);
          if (saved) fetchData(pipelineId, { silent: true });
        }}
        pipeline={currentPipeline}
      />

      {/* Motivo da perda */}
      <Dialog
        open={!!lostDialog}
        onClose={cancelLost}
        maxWidth="xs"
        fullWidth
        classes={{ paper: dialogClasses.paper }}
      >
        <FormDialogHeader
          title="Motivo da perda"
          subtitle="O motivo aparece no relatório do funil."
          onClose={cancelLost}
        />
        <DialogContent dividers className={dialogClasses.content} style={{ gap: 12 }}>
          <div className={classes.reasonChips}>
            {QUICK_LOST_REASONS.map((reason) => (
              <Chip
                key={reason}
                size="small"
                clickable
                label={reason}
                color={lostReason === reason ? "secondary" : "default"}
                variant={lostReason === reason ? "default" : "outlined"}
                onClick={() => setLostReason(reason)}
              />
            ))}
          </div>
          <TextField
            autoFocus
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Ou descreva o motivo…"
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmLost();
            }}
          />
        </DialogContent>
        <DialogActions className={dialogClasses.actions}>
          <Button onClick={cancelLost} className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}>
            Cancelar
          </Button>
          <Button
            onClick={confirmLost}
            variant="contained"
            disableElevation
            className={dialogClasses.actionButton}
            style={{ backgroundColor: theme.palette.error.main, color: "#fff" }}
          >
            Confirmar perda
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cabeçalho */}
      <div className={classes.header}>
        <div>
          <div className={classes.titleRow}>
            <Typography component="h1" className={classes.title}>
              CRM
            </Typography>
            {/* seletor visível apenas quando a empresa tem mais de um funil */}
            {pipelines.length > 1 && (
              <TextField
                select
                variant="outlined"
                size="small"
                className={classes.pipelineSelect}
                value={pipelineId || ""}
                onChange={(e) => setPipelineId(e.target.value)}
                inputProps={{ "aria-label": "Funil" }}
              >
                {pipelines.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </div>
          {!isMobile && (
            <Typography className={classes.subtitle}>
              {currentPipeline?.name ? `${currentPipeline.name} · ` : ""}
              Arraste os cards entre as etapas. Ao soltar em Perdido, o motivo é pedido.
            </Typography>
          )}
        </div>
        <div className={classes.headerButtons}>
          {/* no celular os botões secundários ficam só com o ícone */}
          {isMobile ? (
            <Button
              variant="outlined"
              className={`${classes.headerButton} ${classes.secondaryButton} ${classes.iconOnlyButton}`}
              onClick={() => history.push("/crm/reports")}
              aria-label="Relatório"
              title="Relatório"
            >
              <BarChartIcon fontSize="small" />
            </Button>
          ) : (
            <Button
              variant="outlined"
              className={`${classes.headerButton} ${classes.secondaryButton}`}
              startIcon={<BarChartIcon />}
              onClick={() => history.push("/crm/reports")}
            >
              Relatório
            </Button>
          )}
          {isAdmin &&
            (isMobile ? (
              <Button
                variant="outlined"
                className={`${classes.headerButton} ${classes.secondaryButton} ${classes.iconOnlyButton}`}
                onClick={() => setPipelineModalOpen(true)}
                disabled={!currentPipeline}
                aria-label="Configurar funil"
                title="Configurar funil"
              >
                <TuneIcon fontSize="small" />
              </Button>
            ) : (
              <Button
                variant="outlined"
                className={`${classes.headerButton} ${classes.secondaryButton}`}
                startIcon={<TuneIcon />}
                onClick={() => setPipelineModalOpen(true)}
                disabled={!currentPipeline}
              >
                Configurar funil
              </Button>
            ))}
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={() => openNewDeal(null)}
          >
            {isMobile ? "Novo" : "Novo negócio"}
          </Button>
        </div>
      </div>

      {/* Indicadores */}
      <div className={classes.kpis}>
        <div className={classes.kpi}>
          <span className={classes.kpiLabel}>Em aberto</span>
          <span className={classes.kpiValue}>{formatBRL(kpis.openValue)}</span>
          <span className={classes.kpiCaption}>
            {kpis.open.length} {kpis.open.length === 1 ? "negócio" : "negócios"} no funil
          </span>
        </div>
        <div className={classes.kpi}>
          <span className={classes.kpiLabel}>Ganhos em 30 dias</span>
          <span className={classes.kpiValue} style={{ color: tone("#43A047") }}>
            {formatBRL(kpis.wonValue)}
          </span>
          <span className={classes.kpiCaption}>
            {kpis.won.length} {kpis.won.length === 1 ? "negócio" : "negócios"}
            {kpis.won.length > 0 ? ` · ticket médio ${formatBRL(kpis.wonValue / kpis.won.length)}` : ""}
          </span>
        </div>
        <div className={classes.kpi}>
          <span className={classes.kpiLabel}>Conversão em 30 dias</span>
          <span className={classes.kpiValue}>
            {kpis.conversion === null ? "—" : `${kpis.conversion}%`}
          </span>
          <span className={classes.kpiCaption}>
            {kpis.closedRecently.length
              ? `ganhos ÷ fechados (${kpis.closedRecently.length})`
              : "nenhum negócio fechado no período"}
          </span>
        </div>
        <button
          type="button"
          className={`${classes.kpi} ${classes.kpiClickable}`}
          onClick={() => setOnlyAttention((v) => !v)}
          aria-pressed={onlyAttention}
          style={onlyAttention ? { borderColor: tone("#F59E0B") } : undefined}
          title={onlyAttention ? "Mostrar todos os negócios" : "Mostrar só os que precisam de atenção"}
        >
          <span className={classes.kpiLabel}>Precisam de atenção</span>
          <span className={classes.kpiValue} style={{ color: tone("#F59E0B") }}>
            {kpis.attention}
          </span>
          <span className={classes.kpiCaption}>
            {onlyAttention ? "filtro ativo · clique para ver todos" : `atrasados ou parados há ${IDLE_DAYS}+ dias`}
          </span>
        </button>
      </div>

      {/* Filtros */}
      <div className={classes.toolbar}>
        <TextField
          className={classes.searchField}
          variant="outlined"
          size="small"
          type="search"
          placeholder="Buscar negócio ou contato"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          inputProps={{ "aria-label": "Buscar negócio ou contato" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          className={classes.ownerSelect}
          variant="outlined"
          size="small"
          value={filterUserId}
          onChange={(e) => setFilterUserId(e.target.value)}
          SelectProps={{ displayEmpty: true }}
          inputProps={{ "aria-label": "Responsável" }}
        >
          <MenuItem value="">Todos os responsáveis</MenuItem>
          {user?.id && <MenuItem value={user.id}>Só os meus</MenuItem>}
          {users
            .filter((u) => u.id !== user?.id)
            .map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
        </TextField>
        {onlyAttention && (
          <Chip
            label="Só os que precisam de atenção"
            onDelete={() => setOnlyAttention(false)}
            style={{ backgroundColor: alpha("#F59E0B", 0.16), color: tone("#F59E0B"), fontWeight: 600 }}
          />
        )}

        <div className={classes.zoomGroup}>
          {!isMobile && (
            <>
              <Tooltip title="Diminuir zoom">
                <span>
                  <IconButton size="small" onClick={() => handleZoom(-ZOOM_STEP)} disabled={zoom <= ZOOM_MIN}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography className={classes.zoomLabel}>{Math.round(zoom * 100)}%</Typography>
              <Tooltip title="Aumentar zoom">
                <span>
                  <IconButton size="small" onClick={() => handleZoom(ZOOM_STEP)} disabled={zoom >= ZOOM_MAX}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip
            title={
              <span>
                Cada coluna é um <strong>estágio do funil</strong> e cada card é
                um <strong>negócio</strong> ligado a um contato.
                <br />
                <br />
                Diferente do Kanban de atendimento, o negócio{" "}
                <strong>não recomeça</strong> quando o cliente volta a conversar
                — o estágio fica salvo no contato.
                <br />
                <br />
                Ao arrastar para <strong>Perdido</strong>, o motivo é
                obrigatório. Clique no card para editar ou mudar de etapa.
              </span>
            }
            placement="bottom-end"
            arrow
          >
            <IconButton size="small" aria-label="Ajuda">
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Atualizar board">
            <span>
              <IconButton size="small" onClick={() => fetchData(pipelineId)} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Board */}
      <div className={classes.boardWrapper}>
        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={36} />
          </div>
        )}
        <div className={dndClasses.boardZoom} style={boardZoomStyle(effectiveZoom)}>
          <Board
            data={boardData}
            onCardMoveAcrossLanes={handleCardMove}
            hideCardDeleteIcon
            cardDragClass={dndClasses.cardDragging}
            style={{
              backgroundColor: "transparent",
              height: "100%",
              padding: isMobile ? "0 8px 12px" : "0 16px 16px",
              alignItems: "stretch",
            }}
            components={boardComponents}
          />
        </div>
      </div>
    </div>
  );
};

export default OpportunityKanban;
